import React, {forwardRef, useEffect, useImperativeHandle} from 'react';
import type {FormInstance, FormProps} from './types';
import {FormContext} from './FormContext';
import {useForm} from './useForm';
import FormItem from './FormItem';

const Form = forwardRef<FormInstance, FormProps>((props, ref) => {
  const {
    form,
    initialValues,
    name,
    disabled = false,
    validateTrigger = 'onChange',
    onFieldsChange,
    onValuesChange,
    onFinish,
    onFinishFailed,
    scrollToFirstError = false,
    preserve = true,
    className = '',
    style,
    children,
  } = props;

  const [formInstance] = useForm(form);
  const {
    setCallbacks,
    setInitialValues,
    setScrollToFirstError,
    setPreserve
  } = formInstance.getInternalHooks('FORM_INTERNAL_HOOKS') || {};

  useEffect(() => {
    if (setScrollToFirstError) {
      setScrollToFirstError(scrollToFirstError);
    }
  }, [setScrollToFirstError, scrollToFirstError]);

  useEffect(() => {
    if (setPreserve) {
      setPreserve(preserve);
    }
  }, [setPreserve, preserve]);

  useEffect(() => {
    if (setCallbacks) {
      setCallbacks({
        onFieldsChange,
        onValuesChange,
        onFinish,
        onFinishFailed
      });
    }
  }, [setCallbacks, onFieldsChange, onValuesChange, onFinish, onFinishFailed]);

  const mountRef = React.useRef(false);
  if (!mountRef.current) {
    if (setInitialValues && initialValues) {
      setInitialValues(initialValues, true);
    }
    mountRef.current = true;
  }

  useImperativeHandle(ref, () => formInstance, [formInstance]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    formInstance.submit();
  };

  const formClassName = `form flex flex-col space-y-4 ${className}`.trim();

  return (
    <FormContext.Provider
      value={{
        form: formInstance,
        disabled,
        name,
        validateTrigger,
      }}
    >
      <form onSubmit={handleSubmit} className={formClassName} style={style}>
        {children}
      </form>
    </FormContext.Provider>
  );
}) as React.ForwardRefExoticComponent<FormProps & React.RefAttributes<FormInstance>> & { useForm: typeof useForm; Item: typeof FormItem };

Form.displayName = 'Form';
Form.useForm = useForm;
Form.Item = FormItem;

export default Form;

